import React, { useEffect, useState } from "react";
import { Form, Input, Button, Typography } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { signin } from "../network/api.js";
import { useNavigate } from "react-router-dom";
import { notify, serverError } from "../utils/notify";
import logo from "../assets/ai360-logo-white.svg";

const { Title, Text } = Typography;

// Only honour an internal (same-origin) return path — never an absolute URL.
function safeReturn(raw) {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const returnTo = safeReturn(new URLSearchParams(window.location.search).get("return"));

  useEffect(() => {
    if (localStorage.getItem("accessToken")) {
      window.location.href = returnTo || "/dashboard";
    }
  }, [returnTo]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const { accessToken, refreshToken } = await signin(values);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      notify.toast("Welcome back");
      // Full load so AuthProvider re-fetches the user before /device-login resumes.
      if (returnTo) window.location.href = returnTo;
      else navigate("/dashboard");
    } catch (error) {
      notify.error(serverError(error, "Login failed. Please check your credentials."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Brand panel */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: 48,
          background: "linear-gradient(150deg, #0b1f3a 0%, #14346b 60%, #2f54eb 140%)",
          color: "#fff",
        }}
        className="login-brand"
      >
        <img src={logo} alt="AI360" style={{ width: 260, maxWidth: "70%" }} />
        <Title level={3} style={{ color: "#fff", marginTop: 32, marginBottom: 4 }}>
          Device Management System
        </Title>
        <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 15, textAlign: "center", maxWidth: 380 }}>
          Monitor your camera fleet, track health & alerts, and manage field operations — all in one place.
        </Text>
      </div>

      {/* Form panel */}
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 24,
          background: "#f4f6fb",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 380,
            background: "#fff",
            padding: "36px 32px",
            borderRadius: 16,
            boxShadow: "0 10px 40px rgba(15,32,67,0.08)",
          }}
        >
          <Title level={2} style={{ marginBottom: 4 }}>
            Sign in
          </Title>
          <Text type="secondary">Welcome back. Please enter your details.</Text>

          <Form name="login" layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} requiredMark={false}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input size="large" prefix={<MailOutlined style={{ color: "#9aa5b8" }} />} placeholder="you@company.com" autoComplete="username" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password size="large" prefix={<LockOutlined style={{ color: "#9aa5b8" }} />} placeholder="••••••••" autoComplete="current-password" />
            </Form.Item>

            <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" size="large" block loading={loading}>
                Sign in
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
